import { CircularProgress, Grid, Paper } from '@mui/material';
import { ColumnSizingState } from '@tanstack/react-table';
import { Formik, FormikProps } from 'formik';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { errorMessage } from 'store/actions/messageActions';
import store from 'store/store';
import {
  GetCompaniesBySearchParams,
  Identifier,
  SearchEntityInfoResponse,
} from '../../global/adapters/crpAPI';
import EmptyState from '../../global/components/EmptyState/EmptyState';
import { useLocalTableValues } from '../../global/hooks';
import { useSearchCompanies } from '../adapters/search-companies/queries';
import StepperSearchCompanies from '../components/search-companies/StepperSearchCompanies';
import DataGrid from 'modules/global/components/DataGrid/DataGrid';
import CompaniesColumns from '../components/search-companies/CompaniesColumns';

type Props = React.ComponentPropsWithoutRef<'div'> & {};

const initialFields: GetCompaniesBySearchParams = {
  identificationType: Identifier.NxtEntityId,
  identificationCode: '',
};

const SearchCompanies: React.FC<Props> = () => {
  const { t } = useTranslation(['common', 'useCase', 'validation']);
  const formRef = useRef<FormikProps<GetCompaniesBySearchParams>>();
  const [displayGrid, setDisplayGrid] = React.useState(false);
  const [columnSizing, setColumnSizing] = useLocalTableValues<ColumnSizingState>(
    'column-sizing-search-companies',
    {}
  );

  const { data, isLoading, isFetching, isError } = useSearchCompanies({
    identificationType: formRef?.current?.values.identificationType,
    identificationCode: formRef?.current?.values.identificationCode,
  });

  const handleOnSubmit = () => {
    // This double setDisplay is used to force a formRef update, to refetch with the new values
    setDisplayGrid(false);
    setDisplayGrid(true);
  };

  const CreateArrayWithData = (data: SearchEntityInfoResponse | undefined) => {
    if (!data) return [];
    return [data];
  };

  React.useEffect(() => {
    isError && store.dispatch(errorMessage(t('validation:errorLoadCompany')));
  }, [isError, t]);

  return (
    <Grid container spacing={2} style={{ margin: '20px 0 0 5px' }}>
      <Grid size={{ xs: 4, sm: 3 }}>
        <Formik
          initialValues={initialFields}
          innerRef={formRef as React.Ref<FormikProps<GetCompaniesBySearchParams>>}
          onSubmit={handleOnSubmit}
        >
          {({ submitForm, resetForm, values, errors }: FormikProps<GetCompaniesBySearchParams>) => {
            return (
              <StepperSearchCompanies
                submitForm={submitForm}
                values={values}
                isFetching={isFetching}
                resetForm={resetForm}
                errors={errors}
              />
            );
          }}
        </Formik>
      </Grid>
      <Grid size={{ xs: 8, sm: 9 }}>
        {displayGrid ? (
          isFetching ? (
            <CircularProgress />
          ) : isError ? (
            <EmptyState title={t('validation:errorLoadCompany')} />
          ) : (
            <React.Fragment>
              <Paper>
                <DataGrid.Client
                  columns={CompaniesColumns}
                  columnSizing={columnSizing}
                  data={CreateArrayWithData(data)}
                  isLoading={isLoading}
                  onColumnSizingChange={setColumnSizing}
                  rowSelection={{}}
                />
              </Paper>
            </React.Fragment>
          )
        ) : (
          <EmptyState
            title={t('useCase:entities.emptyState.title')}
            subTitle={t('useCase:entities.emptyState.subTitle')}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default SearchCompanies;
